import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import { cleanEmailText } from '../utils/emailText';
import '../styles/ticketTheme.css';
import './TicketStats.css';

function TicketStats() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (Globals.LoginTime === null) {
      navigate('/login');
      return;
    }

    (async () => {
      try {
        const data = await apiClient.get('/api/tickets/stats');
        // Defends against a backend that hasn't been redeployed with these
        // newer fields yet (frontend/backend deploys aren't atomic) -
        // without this, an old API response with these fields simply
        // missing crashes the whole page on the first .length access.
        setStats({
          unassignedOpenCount: 0,
          breachingCount: 0,
          dueTodayCount: 0,
          recentActivity: [],
          workload: [],
          avgResolutionHours: null,
          avgFirstResponseHours: null,
          onTimeRate: null,
          firstResponseOnTargetRate: null,
          reopenRate: null,
          reopenedCount: 0,
          dailyVolume: [],
          ...data
        });
      } catch (err) {
        console.error('Error fetching ticket stats:', err);
        setError('Failed to load stats');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return <PageShell contentClassName="tk"><p aria-live="polite">Loading stats&hellip;</p></PageShell>;
  }

  if (error || !stats) {
    return <PageShell contentClassName="tk"><div className="stats-error">{error || 'No data available.'}</div></PageShell>;
  }

  const formatResolutionTime = (hours) => {
    if (hours === null || hours === undefined) return 'N/A';
    if (hours < 24) return `${hours.toFixed(1)} hrs`;
    return `${(hours / 24).toFixed(1)} days`;
  };

  const cards = [
    { key: 'open', label: 'Open Right Now', value: stats.openNow, icon: 'fas fa-inbox', to: '/tickets?status=open' },
    { key: 'unassigned', label: 'Unassigned Queue', value: stats.unassignedOpenCount, icon: 'fas fa-user-slash', to: '/tickets?assigned=unassigned' },
    { key: 'breaching', label: 'Breaching (Overdue)', value: stats.breachingCount, icon: 'fas fa-triangle-exclamation', to: '/tickets?due=overdue', warn: stats.breachingCount > 0 },
    { key: 'dueToday', label: "Today's Workload (Due Today)", value: stats.dueTodayCount, icon: 'fas fa-calendar-day', to: '/tickets?due=today' },
    { key: 'created', label: 'Created Today', value: stats.createdToday, icon: 'fas fa-plus-circle' },
    { key: 'closedToday', label: 'Closed Today', value: stats.closedToday, icon: 'fas fa-check-circle' },
    { key: 'closed30', label: 'Closed Last 30 Days', value: stats.closedLast30Days, icon: 'fas fa-calendar-check' },
  ];

  // The three headline numbers sit above the queue counters.
  const featured = [
    { key: 'firstResponse', label: 'Avg first response', hint: 'Last 30 days', value: formatResolutionTime(stats.avgFirstResponseHours) },
    { key: 'resolution', label: 'Avg resolution time', hint: 'Last 30 days', value: formatResolutionTime(stats.avgResolutionHours) },
    { key: 'onTime', label: 'Closed on time', hint: 'Of closed tickets with a due date, last 30 days', value: stats.onTimeRate === null ? 'N/A' : `${stats.onTimeRate}%` },
    { key: 'firstReplyTarget', label: 'First reply on target', hint: 'Replied within the target for its priority, last 30 days', value: stats.firstResponseOnTargetRate === null ? 'N/A' : `${stats.firstResponseOnTargetRate}%` },
    {
      key: 'reopen',
      label: 'Reopen rate',
      hint: stats.reopenRate === null ? 'Appears once tickets have been closed and tracked' : `${stats.reopenedCount} reopened after closing, last 30 days`,
      value: stats.reopenRate === null ? 'N/A' : `${stats.reopenRate}%`
    }
  ];

  const volumeMax = Math.max(1, ...stats.dailyVolume.map((d) => Math.max(d.created, d.closed)));
  const workloadMax = Math.max(1, ...stats.workload.map((r) => r.count));
  const closedMax = Math.max(1, ...stats.closedByStaff.map((r) => r.count));
  const weekday = (iso) => new Date(iso).toLocaleDateString('en-GB', { weekday: 'short' });

  const maxDistribution = Math.max(1, ...stats.satisfaction.distribution.map((d) => d.count));

  return (
    <PageShell contentClassName="tk">
      <div className="stats-page">
        <h1>Support Stats</h1>
        <p className="stats-intro">A snapshot of ticket volume, who's closing them, and how customers are rating the support they got.</p>

        <div className="stats-featured">
          {featured.map((f) => (
            <div key={f.key} className="stats-kpi">
              <div className="stats-kpi-label">{f.label}</div>
              <div className="stats-kpi-value">{f.value}</div>
              <div className="stats-kpi-hint">{f.hint}</div>
            </div>
          ))}
        </div>

        <div className="stats-grid">
          {cards.map((c) => {
            const Wrapper = c.to ? 'button' : 'div';
            return (
              <Wrapper
                key={c.key}
                type={c.to ? 'button' : undefined}
                className={`stats-card ${c.to ? 'stats-card-link' : ''} ${c.warn ? 'stats-card-warn' : ''}`}
                onClick={c.to ? () => navigate(c.to) : undefined}
              >
                <div className="stats-card-icon"><i className={c.icon} aria-hidden="true"></i></div>
                <div>
                  <div className="stats-card-value">{c.value}</div>
                  <div className="stats-card-label">{c.label}</div>
                </div>
              </Wrapper>
            );
          })}
        </div>

        <div className="stats-panels">
          {stats.dailyVolume.length > 0 && (
            <div className="stats-panel stats-panel-wide">
              <div className="stats-panel-head">
                <h2>Tickets per day</h2>
                <div className="stats-legend" aria-hidden="true">
                  <span><i className="stats-swatch stats-swatch-created"></i>Created</span>
                  <span><i className="stats-swatch stats-swatch-closed"></i>Closed</span>
                </div>
              </div>
              <div className="stats-volume" role="img" aria-label={`Tickets created and closed per day over the last ${stats.dailyVolume.length} days`}>
                {stats.dailyVolume.map((d) => (
                  <div key={d.date} className="stats-volume-day" title={`${weekday(d.date)}: ${d.created} created, ${d.closed} closed`}>
                    <div className="stats-volume-bars">
                      <div className="stats-volume-bar stats-volume-created" style={{ height: `${(d.created / volumeMax) * 100}%` }}></div>
                      <div className="stats-volume-bar stats-volume-closed" style={{ height: `${(d.closed / volumeMax) * 100}%` }}></div>
                    </div>
                    <div className="stats-volume-label">{weekday(d.date)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="stats-panel">
            <h2>Recent Activity</h2>
            {stats.recentActivity.length === 0 ? (
              <p className="stats-empty">No activity yet.</p>
            ) : (
              <ul className="stats-activity-feed">
                {stats.recentActivity.map((a) => (
                  <li key={a.id}>
                    <Link to={`/tickets/${a.ticket_id}`} className="stats-activity-link">
                      <div className="stats-activity-row">
                        <span className="stats-activity-author">{a.author_name}</span>
                        {a.internal_external === 'internal' && <span className="stats-activity-internal-badge">Internal</span>}
                        <span className="stats-activity-ticket">#{a.ticket_number ?? ''} {a.ticket_subject || ''}</span>
                      </div>
                      <div className="stats-activity-text">{(() => { const t = cleanEmailText(a.description || '').reply; return t.length > 120 ? `${t.slice(0, 120)}…` : t; })()}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="stats-panel">
            <h2>Open Tickets, By Agent (Workload)</h2>
            {stats.workload.length === 0 ? (
              <p className="stats-empty">No open tickets right now.</p>
            ) : (
              <ul className="stats-leaderboard">
                {stats.workload.map((row) => (
                  <li key={row.name} style={{ '--share': `${(row.count / workloadMax) * 100}%` }}>
                    <span>{row.name}</span>
                    <span className="stats-leaderboard-count">{row.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="stats-panel">
            <h2>Closed in the Last 30 Days, By Staff</h2>
            {stats.closedByStaff.length === 0 ? (
              <p className="stats-empty">Nothing closed in the last 30 days yet.</p>
            ) : (
              <ul className="stats-leaderboard">
                {stats.closedByStaff.map((row) => (
                  <li key={row.name} style={{ '--share': `${(row.count / closedMax) * 100}%` }}>
                    <span>{row.name}</span>
                    <span className="stats-leaderboard-count">{row.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="stats-panel">
            <h2>Customer Satisfaction</h2>
            {stats.satisfaction.totalRatings === 0 ? (
              <p className="stats-empty">No ratings received yet.</p>
            ) : (
              <>
                <div className="stats-satisfaction-summary">
                  <span className="stats-satisfaction-avg">{stats.satisfaction.averageRating.toFixed(1)}</span>
                  <span className="stats-satisfaction-out-of">/ 5 &middot; {stats.satisfaction.totalRatings} rating{stats.satisfaction.totalRatings === 1 ? '' : 's'}</span>
                </div>
                <div className="stats-distribution">
                  {stats.satisfaction.distribution.slice().reverse().map((d) => (
                    <div key={d.score} className="stats-distribution-row">
                      <span className="stats-distribution-label">{d.score} <i className="fas fa-star" aria-hidden="true"></i></span>
                      <div className="stats-distribution-bar-track">
                        <div className="stats-distribution-bar" style={{ width: `${(d.count / maxDistribution) * 100}%` }}></div>
                      </div>
                      <span className="stats-distribution-count">{d.count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

export default TicketStats;
